import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse, Drawer, Space, Spin } from 'antd';
import { useAggregationConfig } from '../../../hooks/SimViewer/useAggregationConfig';
import {
  useDataFilter,
  type LocationFilterType,
} from '../../../hooks/SimViewer/useDataFilter';
import { useMetaData } from '../../../hooks/data/useMetaData';
import { useAreasData } from '../../../hooks/data/useAreasData';
import { useAreaAggregationData } from '../../../hooks/data/useAreaAggregationData';
import { downloadAsCsv } from '../../../utils/file';
import { toPascal } from '../../../utils/case';
import { includes } from '../../../utils/array';
import { BarChart, type BarItem } from '../Chart/BarChart';
import { MapMarkedIconButton } from '../../MapMarkedIconButton';
import { ActionButtons } from './ActionButtons';
import { AreaAggregationComparison } from './AreaAggregationComparison';

type Props = {
  type: Extract<LocationFilterType, 'start' | 'end' | 'home' | 'work'>;
  icon: ReactNode;
  label: string;
  extra?: ReactNode;
};

const AreaRankingPanel = ({
  type,
  icon,
  label,
  extra,
  ...props
}: Props): ReactNode => {
  const { setStopBy } = useDataFilter();
  const { aggregationConfig, startAggregation, clearAggregation } =
    useAggregationConfig();

  return (
    <Collapse.Panel
      key={type}
      header={
        <Space>
          {icon}
          {label}
          {extra}
        </Space>
      }
      extra={
        <MapMarkedIconButton
          active={aggregationConfig?.target === type}
          onClick={(): void => {
            if (aggregationConfig?.target === type) {
              clearAggregation();
            } else {
              startAggregation(type);
            }
            setStopBy(null);
          }}
        />
      }
      {...props}
    >
      <AreaRankingPanelContent type={type} label={label} />
    </Collapse.Panel>
  );
};

const AreaRankingPanelContent = ({
  type,
  label,
}: Pick<Props, 'type' | 'label'>): ReactNode => {
  const { t } = useTranslation();
  const { currentDataMeta } = useMetaData();
  const { dataFilter, updateAreaFilter } = useDataFilter();
  const { findArea } = useAreasData();
  const { aggregationConfig } = useAggregationConfig();
  const metric = includes(
    ['count', 'count_per_area'],
    aggregationConfig?.metric
  )
    ? aggregationConfig?.metric
    : 'count_per_area';
  const { isLoading, aggregations } = useAreaAggregationData(metric, type);

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  const ranking = useMemo(() => {
    return Array.from(aggregations)
      .sort(
        (
          [, value1]: [string, number],
          [, value2]: [string, number]
        ): number => {
          // 集計値の大きい順にソートする
          return -(value1 - value2);
        }
      )
      .slice(0, 10)
      .map(([keyCode, value]: [string, number]): BarItem => {
        return {
          key: keyCode,
          value: value,
          label: findArea(keyCode)?.s_name ?? `(${t('unknown')})`,
        };
      });
  }, [aggregations, findArea, t]);

  return (
    <Spin spinning={isLoading}>
      <BarChart
        disablePercentage
        data={ranking}
        onBarItemClick={(key: string): void => {
          updateAreaFilter([key], {
            [type]: !dataFilter[type].areaKeyCodes.includes(key),
          });
        }}
        noDataMessage={t('message.noTripFound')}
        unit={
          metric === 'count_per_area' ? t('unit.agentPerArea') : t('unit.agent')
        }
      />
      <ActionButtons
        onOpenComparison={(): void => {
          setIsDrawerOpen(true);
        }}
        onExportCsv={(withBom: boolean): void => {
          downloadAsCsv(
            [
              [label, `${t('agentCount')} [${t('unit.agentPerArea')}]`],
              ...ranking.map((row): (string | number)[] => {
                return [row.label, row.value];
              }),
            ],
            `${currentDataMeta?.name ?? ''}_${toPascal(label)}.csv`,
            { withBom: withBom }
          );
        }}
        disabled={isLoading}
      />
      <Drawer
        title={t('barChartTitle', { target: label })}
        placement="right"
        width="80%"
        destroyOnClose
        open={isDrawerOpen}
        onClose={(): void => {
          setIsDrawerOpen(false);
        }}
      >
        <AreaAggregationComparison
          type={type}
          noDataMessage={t('message.noAgentFound')}
          unit={
            metric === 'count_per_area'
              ? t('unit.agentPerArea')
              : t('unit.agent')
          }
        />
      </Drawer>
    </Spin>
  );
};

export { AreaRankingPanel };
